import { memo } from 'react';

import { Row, Spinner, Typography } from 'enable-ui';

import { InviteeDropdownProps } from 'modules/calendar/core/type';
import {
  Avatar,
  DropdownContainer,
  InviteeInfo,
  InviteeItem,
  InviteeList,
  InviteeName,
  InviteeType,
  ScheduleOverlap,
  Tab,
  TabList,
} from 'modules/calendar/styles/invitee-selector';

import { ReactComponent as AvatarIcon } from 'assets/icons/avatar.svg';

import { isEmpty, isNil } from 'lodash';

import { COLOR_CONFIG } from 'constants/color';

const InviteeDropdown = memo(
  ({
    isOpen,
    anchorEl,
    selectedInvitees,
    availableInvitees,
    onSelect,
    activeTab,
    setActiveTab,
    onScroll,
    isLoadingClients,
    isLoadingTrainers,
    onTabChange,
    dropdownRef,
    searchQuery,
  }: InviteeDropdownProps) => {
    const selectedIds = selectedInvitees.map((invitee) => invitee.id);

    if (!isOpen || !anchorEl) return null;

    const currentInvitees =
      activeTab === 'clients'
        ? availableInvitees.clients.items
            .filter((i) => !selectedIds.includes(i.id))
            .filter((i) =>
              searchQuery ? i.name.toLowerCase().includes(searchQuery.toLowerCase()) : true,
            )
        : availableInvitees.trainers.items
            .filter((i) => !selectedIds.includes(i.id))
            .filter((i) =>
              searchQuery ? i.name.toLowerCase().includes(searchQuery.toLowerCase()) : true,
            );

    const isLoading = activeTab === 'clients' ? isLoadingClients : isLoadingTrainers;

    const handleTabClick = (tab: 'clients' | 'trainers') => () => {
      setActiveTab(tab);
      onTabChange?.(tab);
    };

    return (
      <DropdownContainer role="dialog" aria-modal="true" ref={dropdownRef}>
        <TabList role="tablist">
          <Tab
            role="tab"
            aria-selected={activeTab === 'clients'}
            active={activeTab === 'clients'}
            onClick={handleTabClick('clients')}
          >
            Clients
          </Tab>
          <Tab
            role="tab"
            aria-selected={activeTab === 'trainers'}
            active={activeTab === 'trainers'}
            onClick={handleTabClick('trainers')}
          >
            Trainers
          </Tab>
        </TabList>

        <InviteeList role="tabpanel" onScroll={onScroll}>
          {currentInvitees.length > 0 ? (
            currentInvitees.map((invitee) => (
              <InviteeItem
                key={invitee.id}
                isSelected={selectedIds.includes(invitee.id)}
                onClick={() => onSelect(invitee)}
              >
                {isNil(invitee.avatar) || isEmpty(invitee.avatar) ? (
                  <AvatarIcon style={{ width: 32, height: 32 }} />
                ) : (
                  <Avatar src={invitee.avatar} alt={invitee.name} />
                )}
                <InviteeInfo>
                  <InviteeName className="ellipsis">{invitee.name}</InviteeName>
                  <Row className="gap-6">
                    <InviteeType>{invitee.type}</InviteeType>
                    {invitee.hasScheduleOverlap && (
                      <ScheduleOverlap>
                        <Typography
                          style={{ transform: 'translateY(-2px)' }}
                          color={COLOR_CONFIG['neutral-text']}
                        >
                          •
                        </Typography>
                        Schedule overlap
                      </ScheduleOverlap>
                    )}
                  </Row>
                </InviteeInfo>
              </InviteeItem>
            ))
          ) : (
            <Typography
              variant="body-3"
              style={{
                textAlign: 'center',
                padding: '16px 0',
                color: COLOR_CONFIG['neutral-text'],
              }}
            >
              No data
            </Typography>
          )}

          {isLoading && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '6px 0 16px',
              }}
            >
              <Spinner size="lg" />
            </div>
          )}
        </InviteeList>
      </DropdownContainer>
    );
  },
);

export default InviteeDropdown;
